/**
 * The state of the Merge into... flow (D66), shared by the two places that hold the sheet:
 * PlantGrid (the cards view) and PlantQuickLook (a plant opened from a tile, a row, a bed or the
 * season).
 *
 * It holds which plant the sheet is open for and in which mode ('merge' from the card's menu,
 * 'ask' for the prompt after an edit), remembers the answer of a saved edit until the edit form has
 * closed (the prompt must not draw over the form), and scrolls to the kept plant's card once a
 * merge is done. What is a twin is the server's (the plants' `twins`, the edit's answer); the
 * choices are lib/mergePlants.
 *
 * Who uses it: PlantGrid and PlantQuickLook.
 *
 * @module useMergeFlow
 */

import { useCallback, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import type { Plant } from '@/shared/types/plant';
import type { MergePlantSheet } from '../components/dialogs/MergePlantSheet';
import { twinsOfPlant } from './mergePlants';

/** The sheet's props, as the flow hands them. */
export type MergeSheetProps = ComponentProps<typeof MergePlantSheet>;

/** What the flow gives its holder. */
export interface MergeFlow {
  /** Opens the sheet for a plant from its card's menu (the pick or confirm step). */
  openMerge: (plant: Plant) => void;
  /** Remembers a saved edit's answer, to prompt once the form has closed. */
  rememberEdit: (plantObjId: number, result: { success: boolean; twins?: number[] }) => void;
  /** To call when the edit form has closed: raises the prompt if the edit made twins. */
  afterFormClosed: () => void;
  /** Props for <MergePlantSheet />. */
  sheetProps: MergeSheetProps;
}

/** What the sheet is open for. */
interface Target {
  /** The plant that goes. */
  plantId: number;
  /** Its twins, oldest first as the server sent them. */
  twinIds: number[];
  /** The first screen. */
  mode: 'merge' | 'ask';
}

/**
 * The Merge into... flow's state.
 *
 * @returns The openers and the sheet's props.
 */
export function useMergeFlow(): MergeFlow {
  const { plants } = usePlantsContext();
  /** The list as of the latest render, for callbacks that run later. */
  const plantsRef = useRef<Plant[]>(plants ?? []);
  plantsRef.current = plants ?? [];
  /** What the sheet is open for (ids only: the plants are read from the list as it is now), or null when shut. */
  const [target, setTarget] = useState<Target | null>(null);
  /** A saved edit's answer, waiting for the form to close. */
  const pending = useRef<{ plantObjId: number; result: { success: boolean; twins?: number[] } } | null>(null);

  /**
   * Opens the sheet from a card's menu: the plant's twins are the ones the list holds.
   *
   * @param plant - The plant that goes.
   */
  const openMerge = useCallback((plant: Plant): void => {
    setTarget({ plantId: plant.plant_obj_id, twinIds: twinsOfPlant(plant, plantsRef.current).map((p) => p.plant_obj_id), mode: 'merge' });
  }, []);

  /**
   * Keeps a saved edit's answer until the edit form has closed.
   *
   * @param plantObjId - The edited plant.
   * @param result - What editPlant answered.
   */
  const rememberEdit = useCallback((plantObjId: number, result: { success: boolean; twins?: number[] }): void => {
    pending.current = { plantObjId, result };
  }, []);

  /** Raises the prompt for the remembered edit, if it made twins the list holds; otherwise does nothing. */
  const afterFormClosed = useCallback((): void => {
    const saved = pending.current;
    pending.current = null;
    if (!saved) return;
    // The list may not have redrawn with the edit yet, so only ids are kept; the sheet reads the plants as they are by then.
    const twinIds = saved.result.success ? saved.result.twins ?? [] : [];
    if (twinIds.length > 0) setTarget({ plantId: saved.plantObjId, twinIds, mode: 'ask' });
  }, []);

  /** Shuts the sheet. */
  const onClose = useCallback((): void => setTarget(null), []);

  /**
   * Scrolls to the kept plant's card once the list has redrawn without the merged one.
   *
   * @param keptPlantObjId - The plant that stayed.
   */
  const onMerged = useCallback((keptPlantObjId: number): void => {
    // The context has already read the list again; wait for the frame that draws it, then show the card.
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      document.getElementById(`plant-card-${keptPlantObjId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }));
  }, []);

  /** The plant that goes, as the list has it now. */
  const plant = target ? (plants ?? []).find((p) => p.plant_obj_id === target.plantId) ?? null : null;
  /** Its twins, as the list has them now; one the list no longer holds is left out. */
  const twins = target ? target.twinIds.map((id) => (plants ?? []).find((p) => p.plant_obj_id === id)).filter((p): p is Plant => p !== undefined) : [];

  return {
    openMerge,
    rememberEdit,
    afterFormClosed,
    sheetProps: { plant, twins, mode: target?.mode ?? 'merge', onClose, onMerged },
  };
}
